import { loadChineseTextService } from '../account/chat/load_chinese_text_service'
import {
  loadTransliteration,
  normalizePronunciationCharacters,
  transliterationOn
} from '../reading_aids/reading_aid_languages'

const CONFIG_ID = 'flashcard-reading-aid-config'
const TARGET_SELECTOR = '[data-flashcard-reading-aid-side]'
const JAPANESE_REGEX = /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fcf\uf900-\ufaff]/u
const KANJI_REGEX = /[\u3400-\u4dbf\u4e00-\u9fcf\uf900-\ufaff]/u
const CHINESE_REGEX = /[\u3400-\u4dbf\u4e00-\u9fff\u{20000}-\u{3134f}]/u
const FORMATTING_TAGS = new Set(['B', 'DEL'])

let config = null
let configGeneration = 0
const chineseRenderTokens = new WeakMap()

function normalizedReadingAidType(value) {
  if (!value || value === 'disabled') return null
  return normalizePronunciationCharacters(value)
}

function parseConfig() {
  const element = document.getElementById(CONFIG_ID)
  if (!element) return null

  try {
    return JSON.parse(element.textContent)
  } catch (_error) {
    // Never log the JSON or parser error: browser error messages may include a
    // snippet of user-authored flashcard text.
    console.warn('Unable to parse flashcard reading-aid data')
    return null
  }
}

function sideData(side) {
  if (!config || !config[side]) return null
  return config[side]
}

function japaneseSegmentsMatchText(segments, expectedText) {
  if (!Array.isArray(segments) || segments.length === 0) return false

  return segments.every(segment => {
    if (!segment || typeof segment.text !== 'string') return false
    if (segment.segments != null && !Array.isArray(segment.segments)) return false
    if (!Array.isArray(segment.segments) || segment.segments.length === 0) return true

    return segment.segments.every(detail => detail && typeof detail.baseText === 'string')
      && segment.segments.map(detail => detail.baseText).join('') === segment.text
  }) && segments.map(segment => segment.text).join('') === expectedText
}

function japaneseSegmentsSupportType(segments, readingAidType) {
  if (readingAidType === 'hiragana') {
    return segments.every(segment => {
      if (!KANJI_REGEX.test(segment.text)) return true
      if (!Array.isArray(segment.segments) || segment.segments.length === 0) return false

      return segment.segments.every(detail =>
        !KANJI_REGEX.test(detail.baseText) || Boolean(detail.hiragana)
      )
    })
  }

  if (readingAidType === 'romaji') {
    return segments.every(segment => {
      if (!JAPANESE_REGEX.test(segment.text)) return true
      if (segment.combinedRomaji) return true
      if (!Array.isArray(segment.segments) || segment.segments.length === 0) return false

      return segment.segments
        .filter(detail => JAPANESE_REGEX.test(detail.baseText))
        .every(detail => Boolean(detail.romaji))
    })
  }

  return false
}

function formattingRuns(target, expectedText) {
  const runs = []
  let valid = true

  function visit(node, path) {
    if (!valid) return

    const tagName = String(node.tagName || '').toUpperCase()
    if (node.nodeType === 3 || tagName === '#TEXT') {
      if (node.textContent) runs.push({ text: node.textContent, path: path })
      return
    }

    const children = Array.from(node.childNodes || node.children || [])
    if (children.length === 0) {
      if (node.textContent) runs.push({ text: node.textContent, path: path })
      return
    }

    children.forEach(child => {
      const childTag = String(child.tagName || '').toUpperCase()
      if (child.nodeType !== 3 && childTag !== '#TEXT' && !FORMATTING_TAGS.has(childTag)) {
        valid = false
        return
      }

      visit(child, FORMATTING_TAGS.has(childTag) ? path.concat(childTag) : path)
    })
  }

  visit(target, [])
  if (!valid || runs.length === 0 || runs.map(run => run.text).join('') !== expectedText) return null
  return runs
}

function formattingAppender(runs) {
  let runIndex = 0
  let runOffset = 0

  return (parent, text) => {
    let textOffset = 0
    while (textOffset < text.length) {
      const run = runs[runIndex]
      if (!run) return false

      const length = Math.min(text.length - textOffset, run.text.length - runOffset)
      const chunk = text.slice(textOffset, textOffset + length)
      let container = parent
      run.path.forEach(tagName => {
        const wrapper = document.createElement(tagName.toLowerCase())
        container.appendChild(wrapper)
        container = wrapper
      })
      container.appendChild(document.createTextNode(chunk))

      textOffset += length
      runOffset += length
      if (runOffset === run.text.length) {
        runIndex += 1
        runOffset = 0
      }
    }
    return true
  }
}

function appendJapaneseReadingAids(target, segments, readingAidType, appendFormatted = null) {
  const appendBase = appendFormatted || ((parent, text) => {
    parent.appendChild(document.createTextNode(text))
    return true
  })

  segments.forEach(segment => {
    const details = Array.isArray(segment.segments) && segment.segments.length > 0
      ? segment.segments
      : [{ baseText: segment.text }]

    if (readingAidType === 'romaji') {
      const combinedRomaji = segment.combinedRomaji
        || details.map(detail => detail.romaji).filter(Boolean).join(' ')
      appendRomajiGroup(target, details.map(detail => detail.baseText).join(''), combinedRomaji, appendBase)
      return
    }

    details.forEach(detail => {
      const baseText = detail.baseText
      const reading = detail[readingAidType]

      if (!reading || reading === baseText) {
        appendBase(target, baseText)
        return
      }

      const ruby = document.createElement('ruby')
      ruby.className = 'flashcard-reading-aid-ruby'
      appendBase(ruby, baseText)

      const rt = document.createElement('rt')
      rt.textContent = reading
      ruby.appendChild(rt)
      target.appendChild(ruby)
    })
  })
}

function renderJapanese(target, data, readingAidType) {
  if (!japaneseSegmentsMatchText(data.readingAids, data.text)) return false
  if (!japaneseSegmentsSupportType(data.readingAids, readingAidType)) return false
  const runs = formattingRuns(target, data.text)
  if (!runs) return false

  target.textContent = ''
  appendJapaneseReadingAids(target, data.readingAids, readingAidType, formattingAppender(runs))
  return true
}

function chineseRenderStillApplies(target, token, requestConfig, generation, side, expectedText) {
  const targetSide = target.dataset && target.dataset.flashcardReadingAidSide

  return config === requestConfig
    && configGeneration === generation
    && chineseRenderTokens.get(target) === token
    && target.isConnected !== false
    && (!targetSide || targetSide === side)
    && target.textContent === expectedText
}

function renderChinese(target, side, data) {
  if (!CHINESE_REGEX.test(data.text) || target.textContent !== data.text) return false
  const requestConfig = config
  const generation = configGeneration
  const token = {}
  chineseRenderTokens.set(target, token)

  return loadChineseTextService().then(service => {
    if (!chineseRenderStillApplies(target, token, requestConfig, generation, side, data.text)) return false

    const processed = service.processText(data.sourceText || data.text, true)
    if (!Array.isArray(processed)) return false
    if (processed.map(segment => segment.text).join('') !== data.text) return false
    if (!chineseRenderStillApplies(target, token, requestConfig, generation, side, data.text)) return false

    // ChineseTextService escapes source text and emits only its own reading-aid
    // elements plus b/del wrappers. Never parse the flashcard source directly.
    target.innerHTML = processed.map(segment => segment.html).join('')
    return true
  }).catch(() => {
    // Keep the existing server-sanitized DOM and avoid logging card text or
    // loader details from this user-facing fallback path.
    console.warn('Unable to load Chinese flashcard reading aids')
    return false
  })
}

// One stacked word: the base text with its reading under it. `appendBase`
// writes the base text (it may carry the card's formatting runs).
function appendRomajiGroup(target, baseText, reading, appendBase) {
  const group = document.createElement('span')
  group.className = 'flashcard-reading-aid-romaji'

  const base = document.createElement('span')
  base.className = 'flashcard-reading-aid-romaji-base'
  appendBase(base, baseText)
  group.appendChild(base)

  if (reading) {
    const readingEl = document.createElement('span')
    readingEl.className = 'flashcard-reading-aid-romaji-text'
    readingEl.textContent = reading
    group.appendChild(readingEl)
  }

  target.appendChild(group)
}

// Greek and Korean (LAN-870): the romaji layout, one stacked word per
// space-separated word, reading under the word.
function renderTransliteration(target, data, language) {
  const runs = formattingRuns(target, data.text)
  if (!runs) return false
  const requestConfig = config

  return loadTransliteration().then(dom => {
    if (config !== requestConfig || target.textContent !== data.text) return false
    const words = dom.romanizeWords(language, data.text)
    if (!words.some(word => word.reading)) return false

    const append = formattingAppender(runs)
    target.textContent = ''
    words.forEach(({ text, reading }) => {
      if (reading) appendRomajiGroup(target, text, reading, append)
      else append(target, text)
    })
    return true
  }).catch(() => {
    console.warn('Unable to load flashcard transliteration')
    return false
  })
}

export function renderFlashcardReadingAid(target, side) {
  const data = sideData(side)
  if (!target || !data || typeof data.text !== 'string') return

  const readingAidType = normalizedReadingAidType(config.pronunciationCharacters)
  const language = String(config.language || '').toLowerCase()

  if (!readingAidType) {
    return false
  } else if (language === 'japanese') {
    return renderJapanese(target, data, readingAidType)
  } else if (language === 'chinese' && readingAidType === 'pinyin') {
    return renderChinese(target, side, data)
  } else if (transliterationOn(language, readingAidType)) {
    return renderTransliteration(target, data, language)
  }

  return false
}

function initializeReadingAids() {
  configGeneration += 1
  config = parseConfig()
  if (!config) {
    delete window.renderFlashcardReadingAid
    return
  }

  document.querySelectorAll(`${TARGET_SELECTOR}:not([data-flashcard-reading-aid-deferred="true"])`)
    .forEach(target => renderFlashcardReadingAid(target, target.dataset.flashcardReadingAidSide))

  // Listen cards create their foreign-language content only on reveal. The
  // inline reveal action calls this hook after inserting the new text node.
  window.renderFlashcardReadingAid = renderFlashcardReadingAid
}

document.addEventListener('turbolinks:load', initializeReadingAids)

export {
  japaneseSegmentsMatchText,
  japaneseSegmentsSupportType,
  normalizedReadingAidType,
  appendJapaneseReadingAids,
  formattingRuns,
  loadChineseTextService,
  initializeReadingAids
}
